// src/features/assessment/hooks/useAssessmentState.ts

import { useState, useEffect, useCallback } from 'react';
import { TrackId } from '../engine/types';
import { getQuestionsForTrack } from '../data/questions';
import { calculateAssessmentResult, LogicOutput } from '../engine/logic';

export type AssessmentStatus = 'idle' | 'active' | 'complete';

export interface AssessmentState {
  status: AssessmentStatus;
  trackId: TrackId | null;
  answers: Record<string, string>;
  calculation: LogicOutput | null; 
  userEmail?: string; // NEW: Persist email so we know they are unlocked
  timestamp: number;
}

const STORAGE_KEY = 'dopomogai_assessment_v3';

const INITIAL_STATE: AssessmentState = { 
  status: 'idle', 
  trackId: null, 
  answers: {}, 
  calculation: null, 
  timestamp: Date.now() 
};

export const useAssessmentState = () => {
  const [state, setState] = useState<AssessmentState>(() => {
    if (typeof window === 'undefined') return INITIAL_STATE;
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) return JSON.parse(saved);
    } catch (e) {
      console.warn('Failed to parse assessment state', e);
    }
    return INITIAL_STATE;
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  }, [state]);

  const startTrack = useCallback((trackId: TrackId) => {
    setState({
      status: 'active',
      trackId,
      answers: {},
      calculation: null,
      userEmail: undefined, // Reset email on start
      timestamp: Date.now(),
    });
  }, []);

  const answerQuestion = useCallback((questionId: string, optionId: string) => {
    setState((prev) => {
      if (!prev.trackId) return prev;

      const nextAnswers = { ...prev.answers, [questionId]: optionId };
      const questions = getQuestionsForTrack(prev.trackId, nextAnswers);
      
      const allAnswered = questions.every((q) => nextAnswers[q.id]);

      if (allAnswered) {
        const calculation = calculateAssessmentResult(prev.trackId, nextAnswers);
        return { 
          ...prev, 
          answers: nextAnswers, 
          status: 'complete', 
          calculation 
        };
      }

      return { ...prev, answers: nextAnswers, status: 'active' };
    });
  }, []);

  const undoLastAnswer = useCallback(() => {
    setState((prev) => {
      if (prev.status === 'complete') {
        return { ...prev, status: 'active', calculation: null };
      }
      
      const keys = Object.keys(prev.answers);
      if (keys.length === 0) {
        return { ...prev, status: 'idle', trackId: null };
      }

      const newAnswers = { ...prev.answers };
      delete newAnswers[keys[keys.length - 1]];

      return { ...prev, answers: newAnswers };
    });
  }, []);

  const resetAssessment = useCallback(() => {
    setState({ ...INITIAL_STATE, timestamp: Date.now() });
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  // NEW: Persist the email unlock
  const submitEmail = useCallback((email: string) => {
    setState(prev => ({ ...prev, userEmail: email }));
  }, []);

  const getCurrentQuestion = useCallback(() => {
    if (!state.trackId || state.status !== 'active') return null;
    const questions = getQuestionsForTrack(state.trackId, state.answers);
    return questions.find(q => !state.answers[q.id]) || null;
  }, [state.trackId, state.status, state.answers]);

  return {
    state,
    startTrack,
    answerQuestion,
    undoLastAnswer,
    resetAssessment,
    submitEmail, // Export this
    getCurrentQuestion,
  };
};